import { useId, useMemo, useState } from "react";
import { Link } from "react-router";
import { CLOSED_TO_VISITORS_MESSAGE, type PropertyPublic } from "@townsy/contracts";
import { HOME_STAGE_LABELS, formatCents } from "../lib/format";
import {
  directoryCountText,
  directorySort,
  districtName,
  districtOfStreet,
  districtOptions,
  filterDirectory,
  isAvailableToBuy,
  otherDistrictMatches,
  streetOptions,
  type DirectoryType,
} from "../lib/directoryFilters";
import { homeOf, homeTeamOf, isPlotForSale as isForSale, logoFallback, propertyDisplayName } from "../lib/propertyLabels";
import { formatCoins } from "../lib/wallet";
import { LogoTile } from "./LogoTile";
import { storeThemeLabel } from "./storeThemeLogic";
import { VisitWebsiteLink } from "./VisitWebsiteLink";
import { closedToViewer, isPropertyOwner, useViewerActorId } from "./VisitorAccess";
import { CommercialBuyButton } from "./checkout/CommercialOffer";
import { useTestPrices } from "./testPrices";

/**
 * Keyboard-accessible text directory with the same actions as the world (docs/01 section 7):
 * open details, Visit website, Enter (including the three Townsy shops, listed first), the Real
 * Estate office for plots, and commercial Buy / Take over (the same checkout as the town). Stores show their
 * interior theme read-only (ADR-0006; the owner changes it in the 3D town's store panel).
 * World v3 (ADR-0008): every place, street by street, filtered by street, type and "available to buy".
 * World v5 (ADR-0041 decision 9): a district choice (All / Central / Riverside / Hillcrest) comes before the street
 * choice, which lists only the chosen district's streets; every row names its district; a text search that finds
 * places in other districts offers "Search all districts".
 * One continuous town (ADR-0042 decision 5): every place is on the one town street, so in the town "Go there" is the
 * same for every place (it frames the place in Overview); the row's district name says where it is.
 */
export function DirectoryList({
  items,
  onOpen,
  onEnter,
  onGoToRealEstate,
  enterDisabledReason,
  goDisabledReason,
  onCheckoutOpened,
  initialStreet = null,
  initialDistrict = null,
  currentDistrictId = null,
  onGoThere,
  goThereDisabledReason,
}: {
  items: PropertyPublic[];
  /** Open the details panel in the town; when absent the item links to /place/:id. */
  onOpen?: (id: string) => void;
  /** Enter the property from the town; when absent Enter links to the town page. */
  onEnter?: (id: string) => void;
  /** Fast travel to the Real Estate office (in town); when absent it links to the office's page. */
  onGoToRealEstate?: () => void;
  enterDisabledReason?: string | null;
  /** Fast travel works from inside buildings too; only an in-flight move disables it. */
  goDisabledReason?: string | null;
  /** Called when a Buy / Take over checkout opens from this list (the in-town overlay closes itself). */
  onCheckoutOpened?: () => void;
  /** Start filtered to this street (a street name clicked on the town map). */
  initialStreet?: string | null;
  /**
   * Start filtered to this district (world v5): null = All (the 2D fallback and the /directory page); the in-town
   * panel passes the visitor's current district (or the district of `initialStreet`).
   */
  initialDistrict?: string | null;
  /** The district the visitor is standing in (in town): marked "you are here" in the district choice. */
  currentDistrictId?: string | null;
  /** "Go there": frame the place in the overview camera (in town, anywhere in the one town; leaves a building first). */
  onGoThere?: (id: string) => void;
  goThereDisabledReason?: string | null;
}) {
  const testPrices = useTestPrices();
  const [query, setQuery] = useState("");
  const [type, setType] = useState<DirectoryType>("all");
  const [district, setDistrict] = useState<string | null>(initialDistrict);
  const [street, setStreet] = useState<string | null>(initialStreet);
  const [availableOnly, setAvailableOnly] = useState(false);
  const searchId = useId();
  const streetId = useId();
  const ordered = useMemo(() => directorySort(items), [items]);
  const districts = useMemo(() => districtOptions(items), [items]);
  const inDistrict = useMemo(() => (district ? items.filter((p) => p.districtId === district) : items), [items, district]);
  const streets = useMemo(() => streetOptions(inDistrict), [inDistrict]);
  const availableCount = useMemo(() => inDistrict.filter(isAvailableToBuy).length, [inDistrict]);
  const officeId = useMemo(() => items.find((p) => p.type === "CIVIC" && p.civic?.kind === "real_estate_office")?.id ?? null, [items]);
  const filtered = useMemo(() => filterDirectory(ordered, { query, type, district, street, availableOnly }), [ordered, query, type, district, street, availableOnly]);
  const elsewhere = useMemo(() => otherDistrictMatches(items, { query, type, district, street, availableOnly }), [items, query, type, district, street, availableOnly]);
  const filteredActive = Boolean(query.trim()) || type !== "all" || district !== null || street !== null || availableOnly;
  const viewer = useViewerActorId();
  const chooseDistrict = (next: string | null) => {
    setDistrict(next);
    // The street choice lists only the chosen district's streets: a street of another district no longer applies.
    if (next && street && districtOfStreet(street, items) !== next) setStreet(null);
  };

  return (
    <div className="stack">
      <div className="row directory-filters">
        <label className="field" style={{ flex: "1 1 220px" }}>
          <span className="visually-hidden" id={searchId}>
            Search businesses and addresses
          </span>
          <input type="search" placeholder="Search businesses, addresses…" value={query} onChange={(e) => setQuery(e.target.value)} aria-labelledby={searchId} />
        </label>
        <div className="segmented" role="group" aria-label="Filter by type">
          {(["all", "COMMERCIAL", "RESIDENTIAL"] as const).map((t) => (
            <button key={t} type="button" className="btn btn-sm" aria-pressed={type === t} onClick={() => setType(t)}>
              {t === "all" ? "All" : t === "COMMERCIAL" ? "Businesses" : "Homes"}
            </button>
          ))}
        </div>
      </div>
      <div className="row directory-filters">
        <div className="directory-district">
          <span className="small" aria-hidden="true">
            District
          </span>
          <div className="segmented directory-districts" role="group" aria-label="Filter by district" data-testid="directory-district">
            <button
              type="button"
              className="btn btn-sm"
              aria-pressed={district === null}
              onClick={() => chooseDistrict(null)}
              title={`Every district: ${items.length} places`}
              data-testid="directory-district-all"
            >
              All
            </button>
            {districts.map((d) => {
              const here = d.id === currentDistrictId;
              return (
                <button
                  key={d.id}
                  type="button"
                  className="btn btn-sm"
                  aria-pressed={district === d.id}
                  aria-label={here ? `${d.name} (you are here)` : undefined}
                  onClick={() => chooseDistrict(d.id)}
                  title={`${d.name}: ${d.count === 1 ? "1 place" : `${d.count} places`}${here ? " · you are here" : ""}`}
                  data-testid={`directory-district-${d.id}`}
                >
                  {d.name}
                </button>
              );
            })}
          </div>
        </div>
        <label className="directory-street" htmlFor={streetId}>
          <span className="small">Street</span>
          <select id={streetId} value={street ?? ""} onChange={(e) => setStreet(e.target.value || null)} data-testid="directory-street">
            <option value="">
              {district ? `All streets in ${districtName(district)}` : "All streets"} ({inDistrict.length})
            </option>
            {streets.map((s) => (
              <option key={s.name} value={s.name}>
                {s.name} ({s.count})
              </option>
            ))}
            {street && !streets.some((s) => s.name === street) ? <option value={street}>{street} (0)</option> : null}
          </select>
        </label>
        <label className="directory-available">
          <input type="checkbox" checked={availableOnly} onChange={(e) => setAvailableOnly(e.target.checked)} data-testid="directory-available-only" />
          <span>Available to buy only ({availableCount})</span>
        </label>
        {filteredActive ? (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => {
              setQuery("");
              setType("all");
              setDistrict(null);
              setStreet(null);
              setAvailableOnly(false);
            }}
          >
            Show all
          </button>
        ) : null}
      </div>
      <p className="small muted" role="status" style={{ margin: 0 }} data-testid="directory-count">
        {directoryCountText(filtered.length, items.length, street, district)}
      </p>
      {elsewhere > 0 ? (
        <p className="small" style={{ margin: 0 }} data-testid="directory-other-districts">
          {elsewhere === 1 ? "1 more match" : `${elsewhere} more matches`} in other districts.{" "}
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => {
              setDistrict(null);
              setStreet(null);
            }}
            data-testid="directory-search-all"
          >
            Search all districts
          </button>
        </p>
      ) : null}
      <ul className="directory-list" data-testid="directory-list">
        {filtered.map((p) => {
          const civic = p.type === "CIVIC";
          const forSale = isForSale(p);
          const name = civic ? `${p.civic?.name ?? "Town building"} (${p.address})` : propertyDisplayName(p);
          const stage = p.home ? (HOME_STAGE_LABELS[p.home.stage] ?? p.home.stage) : null;
          const typeLabel = civic ? "Town service" : p.type === "COMMERCIAL" ? "Commercial" : "Residential";
          const interior = p.type === "COMMERCIAL" ? storeThemeLabel(p.interiorTheme) : null;
          // ADR-0019: a closed home or store says so; its owner can still go in (the server admits them).
          const closedForMe = closedToViewer(p, viewer);
          const canEnter = p.enterable || (p.access === "closed" && p.status === "OWNED" && isPropertyOwner(p, viewer));
          const notOpenReason = closedForMe ? CLOSED_TO_VISITORS_MESSAGE : "Not open to visitors";
          const where = districtName(p.districtId);
          return (
            <li key={p.id} className="directory-item" data-testid={`directory-item-${p.id}`}>
              <LogoTile placement={p.placement} fallback={logoFallback(p)} />
              <div style={{ minWidth: 0 }}>
                <h3>
                  {onOpen ? (
                    <button type="button" className="btn btn-ghost btn-sm" style={{ padding: 0, fontSize: "1rem" }} onClick={() => onOpen(p.id)}>
                      {name}
                    </button>
                  ) : (
                    <Link to={`/place/${p.id}`}>{name}</Link>
                  )}
                  {p.placement?.isDemo ? (
                    <>
                      {" "}
                      <span className="badge">Demo</span>
                    </>
                  ) : null}
                  {forSale ? (
                    <>
                      {" "}
                      <span className="badge badge-coin">For sale</span>
                    </>
                  ) : null}
                </h3>
                <div className="small muted">
                  {civic ? typeLabel : p.address}
                  {where ? (
                    <>
                      {" · "}
                      <span data-testid={`directory-where-${p.id}`}>{where}</span>
                    </>
                  ) : null}
                  {civic && homeTeamOf(p.civic) ? (
                    <>
                      {" · "}
                      <span data-testid={`directory-team-${p.id}`}>{homeOf(homeTeamOf(p.civic)!)}</span>
                    </>
                  ) : null}
                  {civic ? "" : ` · ${typeLabel}`}
                  {civic ? "" : p.ownerLabel ? ` · owner ${p.ownerLabel}` : forSale ? "" : " · unowned"}
                  {stage ? ` · ${stage}` : ""}
                  {p.placement?.destinationHost ? ` · ${p.placement.destinationHost}` : ""}
                </div>
                {p.placement?.shortDescription ? <div className="small">{p.placement.shortDescription}</div> : null}
                {interior ? (
                  <div className="small muted" data-testid={`directory-interior-${p.id}`}>
                    Interior: {interior}
                  </div>
                ) : null}
                {civic && p.civic?.description ? <div className="small">{p.civic.description}</div> : null}
                {closedForMe ? (
                  <div className="small" data-testid={`directory-closed-${p.id}`}>
                    {CLOSED_TO_VISITORS_MESSAGE}
                  </div>
                ) : null}
                {p.type === "COMMERCIAL" ? (
                  <div className="small price muted">
                    Value {formatCents(p.currentBasisCents)} · next price {formatCents(p.nextPriceCents)}
                    {testPrices ? " (development test prices)" : ""}
                  </div>
                ) : null}
                {forSale && p.forSale ? (
                  <div className="small price muted">
                    {p.forSale.coins !== null ? formatCoins(p.forSale.coins) : ""}
                    {p.forSale.coins !== null && p.forSale.usdCents !== null ? " or " : ""}
                    {p.forSale.usdCents !== null ? formatCents(p.forSale.usdCents) : ""} · buy at Townsy Real Estate, 1 Plaza Way
                  </div>
                ) : null}
              </div>
              <div className="actions">
                {p.type === "COMMERCIAL" && p.placement?.hasWebsite && p.permittedActions.includes("visit_website") ? (
                  <VisitWebsiteLink propertyId={p.id} host={p.placement.destinationHost} className="btn btn-sm btn-primary" />
                ) : null}
                {forSale ? null : onEnter ? (
                  <button
                    type="button"
                    className={civic ? "btn btn-sm btn-primary" : "btn btn-sm"}
                    disabled={!canEnter || Boolean(enterDisabledReason)}
                    title={!canEnter ? notOpenReason : (enterDisabledReason ?? undefined)}
                    onClick={() => onEnter(p.id)}
                    data-testid={`directory-enter-${p.id}`}
                  >
                    Enter
                  </button>
                ) : (
                  <Link
                    className="btn btn-sm"
                    to={`/place/${p.id}`}
                    aria-disabled={!canEnter}
                    title={canEnter ? "Open in the town to enter" : notOpenReason}
                    data-testid={`directory-enter-${p.id}`}
                  >
                    {canEnter ? "Enter (in the town)" : "Closed"}
                  </Link>
                )}
                {onGoThere ? (
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => onGoThere(p.id)}
                    disabled={Boolean(goThereDisabledReason)}
                    title={goThereDisabledReason ?? "Show it from above (Overview camera)"}
                    data-testid={`directory-go-${p.id}`}
                  >
                    Go there
                  </button>
                ) : null}
                {onOpen ? (
                  <button type="button" className="btn btn-sm" onClick={() => onOpen(p.id)}>
                    {civic ? "Details" : "Details & stats"}
                  </button>
                ) : (
                  <Link className="btn btn-sm" to={`/place/${p.id}`}>
                    {civic ? "Details" : "Details & stats"}
                  </Link>
                )}
                {forSale ? (
                  onGoToRealEstate ? (
                    <button type="button" className="btn btn-sm btn-primary" onClick={onGoToRealEstate} disabled={Boolean(goDisabledReason)} title={goDisabledReason ?? undefined}>
                      Go to the Real Estate office
                    </button>
                  ) : (
                    <Link className="btn btn-sm btn-primary" to={officeId ? `/place/${officeId}` : "/"}>
                      Buy at the Real Estate office
                    </Link>
                  )
                ) : p.type === "COMMERCIAL" ? (
                  <CommercialBuyButton item={p} onOpened={onCheckoutOpened} />
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {filtered.length === 0 ? <p className="muted">No places match these filters.</p> : null}
    </div>
  );
}
